import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CompactFilterBar, Input, Badge, Modal, Select, SearchableSelect, LoadingSpinner, Pagination, ErrorSummary } from '@/components/common';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage, getFieldErrors } from '@/services/api';
import { ImportExportControls } from '@/components/admin/ImportExportControls';
import { buildVariantPayload, validateVariantForm } from './variantPayload';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { canManageVehicleMaster } from './masterDataAccess';
import { BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox, useBulkSelection } from '@/components/bulk';
import type { PaginatedResponse } from '@/types';
import { useLiveInsurers } from '@/hooks/useInsurerPlugins';
import { formatCurrency, formatDate } from '@/utils';

interface VehicleModel {
  id: string;
  name: string;
}

interface VehicleVariant {
  id: string;
  model: string;
  model_name: string;
  name: string;
  code: string;
  fuel_type: string;
  transmission: string;
  engine_cc: number;
  cc: number | null;
  gvw: number | null;
  ex_showroom_price: number;
  is_active: boolean;
}

const transmissionOptions = [
  { value: 'manual', label: 'Manual' },
  { value: 'automatic', label: 'Automatic' },
  { value: 'amt', label: 'AMT' },
  { value: 'cvt', label: 'CVT' },
  { value: 'dct', label: 'DCT' },
  // `VehicleVariant.TRANSMISSION_CHOICES` (backend) also has this one; it was
  // missing here, which meant it could be typed into neither the create form
  // nor (now) the list's Transmission filter, and an "imt" row already saved
  // by another route (import, API) had no way to be found or re-selected.
  { value: 'imt', label: 'IMT' },
];

// The list filter's "All ..." option, kept separate from transmissionOptions
// above: the create/edit modal always has a real transmission selected
// ('manual' by default), but a filter needs a value that means "don't filter".
const transmissionFilterOptions = [{ value: '', label: 'All Transmissions' }, ...transmissionOptions];

// Status is a plain is_active boolean -- '' means "don't filter", so the
// value that goes on the wire is only ever 'true' or 'false' once chosen.
const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const initialForm = {
  model: '',
  name: '',
  code: '',
  fuel_type: '',
  transmission: 'manual',
  engine_cc: '',
  cc: '',
  gvw: '',
  ex_showroom_price: '',
};

/** Field names this modal actually renders an input for. */
const FORM_FIELDS = new Set(Object.keys(initialForm));

const itemsPerPage = 20;

/** B11 -- one row of `/vehicles/variants/uncoded/?insurer=<code>`: a live variant
 *  the chosen insurer holds no vehicle code for, so it cannot price it live. */
interface UncodedVariant {
  id: string;
  make: string;
  model: string;
  variant: string;
  code: string;
  category: string;
  fuel: string;
  cc: number | null;
  is_active: boolean;
  quote_count: number;
  created_at: string;
  created_by: string | null;
}

function UncodedVariantsReport() {
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState('');
  // The insurers switched on for motor -- one report per insurer's vehicle master.
  const live = useLiveInsurers('motor');
  const insurers = live.data ?? [];
  const insurer = insurers.find((i) => i.code === picked) ?? insurers[0];
  const code = insurer?.code ?? '';
  const name = insurer?.display_name ?? '';
  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<UncodedVariant>>({
    queryKey: ['vehicle-variants-uncoded', code, page],
    enabled: Boolean(code),
    queryFn: async () => {
      const res = await api.get(
        `/vehicles/variants/uncoded/?insurer=${encodeURIComponent(code)}&page=${page}&page_size=${itemsPerPage}`
      );
      return res.data;
    },
  });
  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const pages = Math.ceil(total / itemsPerPage);

  if (live.isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </Card>
    );
  }
  if (live.isError) {
    return (
      <Card>
        <div role="alert" className="px-6 py-8 text-center">
          <p className="text-sm text-red-700 dark:text-red-400">
            Could not load the switched-on insurers: {getErrorMessage(live.error)}
          </p>
          <Button className="mt-3" variant="outline" size="sm" onClick={() => live.refetch()}>
            Try again
          </Button>
        </div>
      </Card>
    );
  }
  if (!insurer) {
    return (
      <Card>
        <div className="px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          No insurer is switched on for motor, so no vehicle code is needed yet.
        </div>
      </Card>
    );
  }

  return (
    <Card padding="none">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {`Variants with no ${name} code${data ? ` (${total})` : ''}`}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {`A quote on one of these cannot fetch a live ${name} premium. Map each to ${name}'s vehicle master, or merge it into the coded variant it duplicates.`}
          </p>
        </div>
        <div className="w-full sm:w-64">
          <Select
            id="uncoded-insurer"
            label="Insurer"
            value={code}
            options={insurers.map((i) => ({ value: i.code, label: i.display_name }))}
            onChange={(event) => {
              setPicked(event.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        // A failed request is not "every variant is coded".
        <div role="alert" className="px-6 py-8 text-center">
          <p className="text-sm text-red-700 dark:text-red-400">
            Could not load the uncoded variants: {getErrorMessage(error)}
          </p>
          <Button className="mt-3" variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          {`Every live variant has a ${name} vehicle code.`}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Make', 'Model', 'Variant', 'Category', 'Fuel', 'CC', 'Quotes', 'Created', 'Created by'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{row.make}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{row.model}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{row.variant}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{row.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{row.fuel || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{row.cc ?? '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{row.quote_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {row.created_at ? formatDate(row.created_at) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {row.created_by ?? 'Not recorded'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </Card>
  );
}

export function VehicleVariantsPage() {
  const queryClient = useQueryClient();
  // Read-only for everyone but the country admin -- which is exactly
  // where the server draws the line. See ./masterDataAccess.ts.
  const canManage = canManageVehicleMaster(useAuthStore().user?.role);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [formData, setFormData] = useState(initialForm);
  const [showUncoded, setShowUncoded] = useState(false);
  // Client-side required-field misses AND the backend's per-field messages
  // share this map, keyed by the serializer's field name.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // List filters (COUNTRY-10 / 2026-09-18 batch, item 15). Engine CC, CC and
  // GVW are plain number boxes rather than dropdowns, matching the precedent
  // set for VehicleModelsPage's Seating Capacity filter: the values in use
  // are whatever has been imported, there is no endpoint that enumerates
  // them, and a hardcoded choice list would quietly hide any variant whose
  // value isn't on it.
  const [filterModel, setFilterModel] = useState('');
  const [filterFuel, setFilterFuel] = useState('');
  const [filterTransmission, setFilterTransmission] = useState('');
  const [filterEngineCc, setFilterEngineCc] = useState('');
  const [filterCc, setFilterCc] = useState('');
  const [filterGvw, setFilterGvw] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<VehicleVariant>>({
    queryKey: [
      'vehicle-variants',
      page,
      debouncedSearch,
      filterModel,
      filterFuel,
      filterTransmission,
      filterEngineCc,
      filterCc,
      filterGvw,
      filterStatus,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterModel) params.set('model', filterModel);
      if (filterFuel) params.set('fuel_type', filterFuel);
      if (filterTransmission) params.set('transmission', filterTransmission);
      if (filterEngineCc) params.set('engine_cc', filterEngineCc);
      if (filterCc) params.set('cc', filterCc);
      if (filterGvw) params.set('gvw', filterGvw);
      if (filterStatus) params.set('is_active', filterStatus);
      const res = await api.get(`/vehicles/variants/?${params.toString()}`);
      return res.data;
    },
  });
  const variants = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const visibleIds = variants.map((v) => v.id);
  const selection = useBulkSelection(visibleIds);
  const activeFilterCount = [
    filterModel,
    filterFuel,
    filterTransmission,
    filterEngineCc,
    filterCc,
    filterGvw,
    filterStatus,
  ].filter(Boolean).length;

  const { data: models = [] } = useQuery<VehicleModel[]>({
    queryKey: ['vehicle-models-lookup'],
    queryFn: async () => {
      // Unpaginated lookup endpoint returns a plain array of {id, name, code, ...}
      const res = await api.get<VehicleModel[]>('/vehicles/models/lookup/');
      return res.data;
    },
  });

  const { data: fuelTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['vehicle-fuel-types-lookup'],
    queryFn: async () => {
      // Unpaginated lookup endpoint returns a plain array of {id, name, code}
      const res = await api.get<{ id: string; name: string }[]>('/vehicles/fuel-types/lookup/');
      return res.data;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicle-variants'] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const payload = buildVariantPayload(data, { mode: 'update' });
        const res = await api.patch(`/vehicles/variants/${editingId}/`, payload);
        return res.data;
      }
      const res = await api.post('/vehicles/variants/', buildVariantPayload(data, { mode: 'create' }));
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Vehicle variant updated' : 'Vehicle variant created');
      setShowModal(false);
      setEditingId(null);
      setFormData(initialForm);
      setFieldErrors({});
    },
    onError: (error) => {
      // Surface the backend's per-field messages ON the fields (F3): a bare
      // toast could not say WHICH field the server rejected, which is how the
      // blank Fuel Type stayed invisible for so long.
      const parsed = getFieldErrors(error);
      setFieldErrors(parsed);
      if (Object.keys(parsed).length === 0) {
        toast.error(getErrorMessage(error));
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/variants/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Vehicle variant deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (variant: VehicleVariant) => {
    setEditingId(variant.id);
    setFormData({
      model: variant.model,
      name: variant.name,
      code: variant.code,
      fuel_type: variant.fuel_type || '',
      transmission: variant.transmission || 'manual',
      engine_cc: variant.engine_cc != null ? String(variant.engine_cc) : '',
      cc: variant.cc != null ? String(variant.cc) : '',
      gvw: variant.gvw != null ? String(variant.gvw) : '',
      ex_showroom_price: variant.ex_showroom_price != null ? String(variant.ex_showroom_price) : '',
    });
    setFieldErrors({});
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this vehicle variant? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
    setFieldErrors({});
  };

  // Edit a control -> its error message goes away. Without this the message
  // that named the field (the whole point of F3) stayed on screen after the
  // user had already fixed it, until the next submit.
  const setField = (field: keyof typeof initialForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Fuel Type is a NOT NULL FK server-side, so catch it here rather than let
    // DRF answer with "This field may not be null." (F3).
    const errors = validateVariantForm(formData);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    createMutation.mutate(formData);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(initialForm);
    setFieldErrors({});
    setShowModal(true);
  };

  // Messages with no input to sit under -- DRF non_field_errors/detail, or a
  // field this modal does not render -- head the form as a banner so a server
  // rejection is never swallowed. The raw field name is deliberately NOT
  // prefixed onto the text (global rule 6: no jargon leak).
  const summaryErrors = Object.entries(fieldErrors)
    .filter(([field]) => !FORM_FIELDS.has(field))
    .map(([, message]) => message);

  const modelOptions = models.map((m) => ({ value: m.id, label: m.name }));
  const fuelTypeOptions = fuelTypes.map((f) => ({ value: f.id, label: f.name }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vehicle Variants</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage vehicle variants</p>
        </div>
        {canManage && (
        <div className="flex items-center gap-2">
          <Button variant={showUncoded ? 'secondary' : 'outline'} onClick={() => setShowUncoded((v) => !v)}>
            {showUncoded ? 'Hide missing insurer code' : 'Missing insurer code'}
          </Button>
          <ImportExportControls apiEndpoint="/vehicles/variants/" invalidateKeys={['vehicle-variants']} />
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={openCreateModal}>
            Add Variant
          </Button>
        </div>
        )}
      </div>

      {canManage && showUncoded && <UncodedVariantsReport />}

      <CompactFilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search variants..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterModel('');
          setFilterFuel('');
          setFilterTransmission('');
          setFilterEngineCc('');
          setFilterCc('');
          setFilterGvw('');
          setFilterStatus('');
          setPage(1);
        }}
        columns={6}
      >
        <SearchableSelect
          id="variant-filter-model"
          label="Model"
          options={modelOptions}
          value={filterModel}
          onChange={(value) => {
            setFilterModel(value);
            setPage(1);
          }}
          placeholder="All Models"
        />
        <Select
          id="variant-filter-fuel"
          label="Fuel"
          options={[{ value: '', label: 'All Fuels' }, ...fuelTypeOptions]}
          value={filterFuel}
          onChange={(e) => {
            setFilterFuel(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="variant-filter-transmission"
          label="Transmission"
          options={transmissionFilterOptions}
          value={filterTransmission}
          onChange={(e) => {
            setFilterTransmission(e.target.value);
            setPage(1);
          }}
        />
        <Input
          id="variant-filter-engine-cc"
          label="Engine CC"
          type="number"
          min={0}
          placeholder="Any"
          value={filterEngineCc}
          onChange={(e) => {
            setFilterEngineCc(e.target.value);
            setPage(1);
          }}
        />
        <Input
          id="variant-filter-cc"
          label="CC"
          type="number"
          min={0}
          placeholder="Any"
          value={filterCc}
          onChange={(e) => {
            setFilterCc(e.target.value);
            setPage(1);
          }}
        />
        <Input
          id="variant-filter-gvw"
          label="GVW (kg)"
          type="number"
          min={0}
          placeholder="Any"
          value={filterGvw}
          onChange={(e) => {
            setFilterGvw(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="variant-filter-status"
          label="Status"
          options={statusOptions}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        />
      </CompactFilterBar>

      {canManage && (
        <BulkActionBar
          resource="vehicles/variants"
          entityName="variants"
          selectedIds={selection.selectedIds}
          onChanged={invalidate}
          onClear={selection.clear}
        />
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are no vehicle variants" -- those
          // are two different states and this platform has a habit of
          // confusing them.
          <div role="alert" className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Could not load vehicle variants</h3>
            <p className="mt-2 text-gray-500">{getErrorMessage(error)}</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : variants.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No vehicle variants found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a vehicle variant.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {canManage && (
                      <th className="px-6 py-3 text-left">
                        <BulkSelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Fuel</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Transmission</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Engine CC</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">CC</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">GVW (kg)</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {variants.map((variant) => (
                    <tr key={variant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {canManage && (
                        <td className="px-6 py-4">
                          <BulkSelectCheckbox
                            checked={selection.isSelected(variant.id)}
                            onChange={() => selection.toggle(variant.id)}
                            label={`Select ${variant.name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{variant.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.model_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {fuelTypes.find((f) => f.id === variant.fuel_type)?.name || variant.fuel_type || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.transmission || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.engine_cc || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.cc ?? '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{variant.gvw ?? '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {variant.ex_showroom_price ? formatCurrency(variant.ex_showroom_price) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={variant.is_active ? 'success' : 'warning'}>
                          {variant.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                          <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(variant)}>
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(variant.id)}>
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Vehicle Variant' : 'Add Vehicle Variant'} size="lg">
        {/* noValidate: the required set is exactly what validateVariantForm
            checks, and our per-field messages name the field and stay on
            screen -- the native bubble does neither. */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <ErrorSummary messages={summaryErrors} />
          <SearchableSelect
            id="model"
            label="Model"
            options={modelOptions}
            value={formData.model}
            onChange={(value) => setField('model', value)}
            placeholder="Select a model"
            error={fieldErrors.model}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Name"
              name="name"
              value={formData.name}
              onChange={(e) => setField('name', e.target.value)}
              error={fieldErrors.name}
              required
            />
            <Input
              label="Code"
              name="code"
              value={formData.code}
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
              error={fieldErrors.code}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* With no placeholder this select rendered the first fuel type as
                if it were chosen while formData.fuel_type was still '' — which
                is why the failure looked like "I filled everything in". */}
            <Select
              label="Fuel Type"
              name="fuel_type"
              options={fuelTypeOptions}
              value={formData.fuel_type}
              onChange={(e) => setField('fuel_type', e.target.value)}
              placeholder="Select a fuel type"
              error={fieldErrors.fuel_type}
              required
            />
            <Select
              label="Transmission"
              name="transmission"
              options={transmissionOptions}
              value={formData.transmission}
              onChange={(e) => setField('transmission', e.target.value)}
              error={fieldErrors.transmission}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Engine CC"
              name="engine_cc"
              type="number"
              value={formData.engine_cc}
              onChange={(e) => setField('engine_cc', e.target.value)}
              error={fieldErrors.engine_cc}
            />
            <Input
              label="Ex-Showroom Price"
              name="ex_showroom_price"
              type="number"
              value={formData.ex_showroom_price}
              onChange={(e) => setField('ex_showroom_price', e.target.value)}
              error={fieldErrors.ex_showroom_price}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="CC (cubic capacity)"
              name="cc"
              type="number"
              value={formData.cc}
              onChange={(e) => setField('cc', e.target.value)}
              error={fieldErrors.cc}
            />
            <Input
              label="GVW (kg)"
              name="gvw"
              type="number"
              value={formData.gvw}
              onChange={(e) => setField('gvw', e.target.value)}
              error={fieldErrors.gvw}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default VehicleVariantsPage;
